import { Building2, Check, ChevronDown } from 'lucide-react';
import { cn } from '../../lib/cn';
import { queryClient } from '../../lib/queryClient';
import { selectActiveOrg, useAuthStore } from './store';
import { useDropdown } from './useDropdown';

/**
 * Topbar org switcher. Reads orgs from the user's `orgs` claim; the selection
 * is persisted (financials-active-org) and sent as X-Organization by the api
 * client. Switching invalidates every query so all data refetches org-scoped.
 */
export function OrgSwitcher() {
  const user = useAuthStore((s) => s.user);
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const setActiveOrg = useAuthStore((s) => s.setActiveOrg);
  const { open, setOpen, containerRef } = useDropdown<HTMLDivElement>();

  const orgs = user?.orgs ?? [];
  const activeOrg = selectActiveOrg({ user, activeOrgId });
  if (!user || orgs.length === 0) return null;

  // ⚠ One org = no control (Jesse, 2026-08-19: "we don't need an agency
  // selector when there is only one option"). A chip that names the only
  // possible answer is furniture; the rail already shows whose account this
  // is. The SWITCHER appears the moment a second org exists.
  if (orgs.length === 1) return null;

  const choose = (orgId: string) => {
    setOpen(false);
    if (orgId === activeOrg?.id) return;
    setActiveOrg(orgId);
    // Every query is org-scoped — refetch the world under the new tenant.
    void queryClient.invalidateQueries();
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cn(
          'inline-flex items-center gap-2 rounded-lg border border-grey-200 bg-white px-3 py-1.5 text-sm font-semibold text-secondary transition-colors hover:bg-grey-50',
          open && 'bg-grey-50',
        )}
        title="Switch organization"
      >
        <Building2 className="h-4 w-4 text-grey-400" aria-hidden />
        <span className="hidden max-w-40 truncate sm:inline">{activeOrg?.name}</span>
        <ChevronDown
          className={cn('h-3.5 w-3.5 text-grey-400 transition-transform', open && 'rotate-180')}
          aria-hidden
        />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Organizations"
          className="absolute right-0 top-full z-40 mt-2 w-64 overflow-hidden rounded-xl border border-grey-200 bg-white py-1 shadow-elevated animate-scale-in"
        >
          <p className="px-4 pb-1 pt-2 text-caption font-semibold uppercase tracking-wide text-grey-400">
            Organization
          </p>
          {orgs.map((org) => {
            const isActive = org.id === activeOrg?.id;
            return (
              <button
                key={org.id}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => choose(org.id)}
                className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-grey-50"
              >
                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      'block truncate text-sm text-secondary',
                      isActive ? 'font-bold' : 'font-medium',
                    )}
                  >
                    {org.name}
                  </span>
                  <span className="block truncate text-caption capitalize text-grey-500">
                    {org.role}
                  </span>
                </span>
                {isActive && <Check className="h-4 w-4 shrink-0 text-accent" aria-hidden />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
